import { useIntl } from 'react-intl-next';
import { usePathParam } from 'react-resource-router';

import { useDefaultCatch } from '@atlassian/eoc-catch';
import { assert, AssertionError, errorToastProps } from '@atlassian/eoc-common';
import { useFlags } from '@atlassian/eoc-global-context';
import {
	routingRulesResource,
	type RoutingRulesResponse,
} from '@atlassian/eoc-routing-rules/resources';
import { useResource } from '@atlassian/eoc-rrr-helpers';

import {
	type CreateEscalationParams,
	type EscalationEntity,
	type SaveViewParams,
	type TeamEscalationsService,
	type UpdateEscalationParams,
	type UpdatePartialEscalationParams,
} from '../../common/types';
import { useEscalationsCommonService } from '../escalations-common';

import { completeTaskEndpoint, saveViewEndpoint } from './endpoints';
import messages from './messages';

export const useTeamEscalationsService = (): TeamEscalationsService => {
	const defaultCatch = useDefaultCatch();
	const flagApi = useFlags();
	const { formatMessage } = useIntl();

	const { update } = useResource(routingRulesResource);
	const [teamId] = usePathParam('teamId');
	const escalationsCommonService = useEscalationsCommonService();

	assert(
		!!teamId,
		new AssertionError('useTeamEscalationsService', "Missing required 'teamId' parameter"),
	);

	const createEscalation = (params: CreateEscalationParams) => {
		return escalationsCommonService.createEscalation(params).then((res) => {
			update((data) => ({
				...data,
				escalations: {
					...data.escalations,
					entities: [...data.escalations.entities, res],
				},
			}));

			return res;
		});
	};

	const updateEscalation = (params: UpdateEscalationParams) => {
		return escalationsCommonService.updateEscalation(params).then((res) => {
			update((data) => ({
				...data,
				escalations: {
					...data.escalations,
					entities: data.escalations.entities.map((entity) => {
						if (entity.id === res.id) {
							return res;
						}

						return entity;
					}),
				},
			}));

			return res;
		});
	};

	const updatePartialEscalation = (params: UpdatePartialEscalationParams) => {
		return escalationsCommonService.updatePartialEscalation(params).then((res) => {
			update((data) => ({
				...data,
				escalations: {
					...data.escalations,
					entities: data.escalations.entities.map((entity) => {
						if (entity.id === res.id) {
							return res;
						}

						return entity;
					}),
				},
			}));

			return res;
		});
	};

	const deleteEscalation = (id: string, name: string) => {
		return escalationsCommonService.deleteEscalation(id, name).then(() => {
			update((data) => ({
				...data,
				escalations: {
					...data.escalations,
					entities: data.escalations.entities.filter((entity) => entity.id !== id),
				},
			}));
		});
	};

	const saveView = (data: RoutingRulesResponse) => {
		update(() => data);

		const view: SaveViewParams = {
			collapsed: data.escalations.collapsed,
			preferences: data.escalations.entities.map(({ id, collapsed }: EscalationEntity) => ({
				id,
				collapsed,
			})),
		};

		return saveViewEndpoint(teamId, view).catch((error) => {
			defaultCatch(
				{
					id: 'useTeamEscalationsService-saveView',
					httpErrorHandler: (err) => {
						flagApi.showFlag({
							...errorToastProps(),
							description: err.message,
							title: formatMessage(messages.saveViewError),
							actions: [
								{
									content: formatMessage(messages.refresh),
									onClick: () => window.location.reload(),
								},
							],
						});
					},
				},
				error,
			);

			throw error;
		});
	};

	const completeTask = () => completeTaskEndpoint(teamId);

	return {
		...escalationsCommonService,
		createEscalation,
		deleteEscalation,
		saveView,
		updateEscalation,
		updatePartialEscalation,
		completeTask,
	};
};
